import { type Comments as CommentsProps } from '@web/graphql-components'
import PagePaginatedComments from '@web/components/page-components/Comments/PaginatedComments'
import { getCommentsQueryOptions, mapComment } from './utils'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { useQueryClient } from '@tanstack/react-query'
import { CommentsQuery } from '@web/graphql-queries/graphql'
import { useEffect, useMemo, useState } from 'react'
import { toSearchParams } from 'apps/fe-donation/utils'
import { GIVEEASY_CORRELATION_HEADER } from 'apps/fe-donation/app/api/utils'
import { v4 } from 'uuid'

const PAGE_SIZE = 21

const PaginatedComments = (
  props: CommentsProps & { containerDimensions: number; isHorizontalLayout?: boolean; isMobile?: boolean },
) => {
  const campaignDomain = usePageStore((state) => state.campaignDomain) ?? ''
  const options = getCommentsQueryOptions(props)
  const queryClient = useQueryClient()
  const [pages, setPages] = useState<CommentsQuery['comments']['comments'][]>([])
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const [isFetching, setIsFetching] = useState(false)

  const fetchComments = async (page: number) => {
    const { comments } = await queryClient.fetchQuery({
      queryKey: ['comments-infinite', options, page],
      queryFn: async () => {
        const data = fetch(
          `/api/comments?${toSearchParams({
            ...options,
            pagination: JSON.stringify({
              pageNumber: page,
              pageSize: PAGE_SIZE,
            }),
          })}`,
          {
            headers: {
              'Content-Type': 'application/json',
              [GIVEEASY_CORRELATION_HEADER]: v4(),
            },
            next: {
              revalidate: 60,
            },
          },
        )
          .then((res) => res.json())
          .catch(() => ({ comments: [] }))

        return data
      },
    })

    setPages((prev) => {
      const newPages = [...prev]
      newPages[page] = comments?.comments || []

      return newPages
    })
    setTotalRecords(comments?.totalRecords || 0)
    setIsFetching(false)
  }

  const [pageNumber, setPageNumber] = useState(1)
  const numberOfRows =
    props.styles?.options?.numberOfRows && props.isHorizontalLayout ? Number(props.styles?.options?.numberOfRows) : 1
  const maxCommentsToShow =
    Math.floor(props.containerDimensions / (props.isHorizontalLayout ? 286 : 150)) * numberOfRows
  const finalPageNumber = Math.ceil((totalRecords ?? 0) / maxCommentsToShow)

  useEffect(() => {
    const startIndex = (pageNumber - 1) * maxCommentsToShow
    const endIndex = pageNumber * maxCommentsToShow

    const startPage = Math.floor(startIndex / PAGE_SIZE)
    const endPage = Math.floor(endIndex / PAGE_SIZE)

    setIsFetching(true)

    // Fetch all needed pages
    Promise.all(Array.from({ length: endPage - startPage + 1 }, (_, i) => fetchComments(startPage + i))).finally(() =>
      setIsFetching(false),
    )
  }, [pageNumber, maxCommentsToShow])

  const commentsToShow = useMemo(() => {
    const startIndex = (pageNumber - 1) * maxCommentsToShow
    const endIndex = pageNumber * maxCommentsToShow

    const result: CommentsQuery['comments']['comments'][0][] = []
    for (let i = startIndex; i < endIndex && i < totalRecords; i++) {
      const pageIndex = Math.floor(i / PAGE_SIZE)
      const itemIndex = i % PAGE_SIZE
      if (pages[pageIndex]?.[itemIndex]) {
        result.push(pages[pageIndex][itemIndex])
      }
    }
    return result
  }, [pages, pageNumber, maxCommentsToShow, totalRecords])

  return (
    <PagePaginatedComments
      {...props}
      comments={commentsToShow.map((c) => mapComment(c, props, campaignDomain)) ?? []}
      currentPageNumber={pageNumber}
      finalPageNumber={finalPageNumber}
      pageSize={Math.floor(maxCommentsToShow / numberOfRows)}
      loadingSlots={
        isFetching && commentsToShow.length < maxCommentsToShow ? maxCommentsToShow - commentsToShow.length : 0
      }
      goToPage={(number) => setPageNumber(number)}
    />
  )
}

export default PaginatedComments
